import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import { User } from "react-feather";
import { FORMS, MODULES } from "../../const";
import { bankBranchValidation } from "../validations/BankBranchCreation";
import LoadingSpinner from "../LoadingSpinner";
import Input from "../input/Input";
import Dropdown from "../input/Dropdown";
import InfoList from "../InfoList";
import { formatFromJsonDate } from "../../dateConfig";
import SearchDropdown from "../input/SearchDropdown";
import { bankContains } from "../../api/bankApi";

const FORM = FORMS.BANK_BRANCH.BNK_BRN.form;

const BankBranchAddComponent = forwardRef(
  ({ errors, dropdown, isLoading, currentValues, editMode }, ref) => {
    const [bankBranchData, setBankBranchData] = useState(
      editMode
        ? {
            ...currentValues,
          }
        : {
            brnCode: "",
            brnName: "",
            brnBankReferance: "",
            brnBank: "",
            brnBankCode: "",
            brnDescription: "",
            brnStatus: 2,
            brnActive: 1,
          }
    );

    const [currentErrors, setCurrentErrors] = useState({});

    useEffect(() => {
      setCurrentErrors(errors || {});
    }, [errors]);

    useImperativeHandle(ref, () => ({
      getData: () => ({
        ...bankBranchData,
      }),
      validateForm: () => {
        return bankBranchValidation(bankBranchData);
      },
      resetForm: () => {
        setBankBranchData({
          brnCode: "",
          brnName: "",
          brnBankReferance: "",
          brnBank: "",
          brnBankCode: "",
          brnDescription: "",
          brnStatus: 2,
          brnActive: 1,
        });
      },
    }));

    return (
      <>
        {isLoading ? (
          <LoadingSpinner />
        ) : (
          <form
            className="ui form"
            style={{ overflowY: "auto", height: "75vh" }}
          >
            <div className="ui segment">
              <h4 className="ui dividing header">Bank Branch Information</h4>

              <div className="two fields">
                <Input
                  label="Branch Code"
                  placeholder="Branch Code"
                  module={MODULES.Master}
                  form={FORM}
                  field="brn_code"
                  editMode={editMode}
                  disabled={true}
                  readOnly={true}
                  name="brnCode"
                  value={bankBranchData.brnCode || ""}
                />
                <Input
                  required={true}
                  label="Branch Name"
                  placeholder="Enter Branch Name"
                  module={MODULES.Master}
                  form={FORM}
                  field="brn_name"
                  editMode={editMode}
                  error={currentErrors.brnName}
                  resetError={() =>
                    setCurrentErrors((prevState) => {
                      const { brnName: _, ...rest } = prevState;
                      return rest;
                    })
                  }
                  name="brnName"
                  value={bankBranchData.brnName}
                  onChange={(e) => {
                    setBankBranchData((prevState) => ({
                      ...prevState,
                      brnName: e.target.value,
                    }));
                  }}
                  blockPattern={/[^a-zA-Z\s&_\-()]/g}
                />
              </div>

              <div className="two fields">
                <SearchDropdown
                  required={true}
                  editMode={editMode}
                  module={MODULES.Master}
                  form={FORM}
                  field="brn_bank_referance"
                  threshold={0}
                  clearable={true}
                  onResultSelect={(data) => {
                    setBankBranchData((prev) => ({
                      ...prev,
                      brnBankReferance: data,
                      brnBank: data,
                    }));
                  }}
                  onClear={() => {
                    setBankBranchData((prev) => ({
                      ...prev,
                      brnBankReferance: "",
                      brnBank: "",
                    }));
                  }}
                  api={bankContains}
                  fieldInfo={{
                    tableName: "masterdata.tms_vm_banks",
                    primaryKey: "bnk_id",
                    fieldName: "bnk_name",
                    id: bankBranchData.brnBankReferance,
                  }}
                  label="Bank"
                  error={currentErrors.brnBankReferance}
                  resetError={() =>
                    setCurrentErrors((prevState) => {
                      const { brnBankReferance: _, ...rest } = prevState;
                      return rest;
                    })
                  }
                />
                <Input
                  label="Bank Code"
                  placeholder="Enter Bank Code"
                  module={MODULES.Master}
                  form={FORM}
                  field="brn_bank_code"
                  editMode={editMode}
                  error={currentErrors.brnBankCode}
                  resetError={() =>
                    setCurrentErrors((prevState) => {
                      const { brnBankCode: _, ...rest } = prevState;
                      return rest;
                    })
                  }
                  name="brnBankCode"
                  value={bankBranchData.brnBankCode}
                  onChange={(e) => {
                    setBankBranchData((prevState) => ({
                      ...prevState,
                      brnBankCode: e.target.value,
                    }));
                  }}
                />
              </div>

              <div className="field">
                <Input
                  label="Description"
                  placeholder="Enter Branch Description"
                  module={MODULES.Master}
                  form={FORM}
                  field="brn_description"
                  editMode={editMode}
                  error={currentErrors.brnDescription}
                  resetError={() =>
                    setCurrentErrors((prevState) => {
                      const { brnDescription: _, ...rest } = prevState;
                      return rest;
                    })
                  }
                  name="brnDescription"
                  value={bankBranchData.brnDescription}
                  onChange={(e) => {
                    setBankBranchData((prevState) => ({
                      ...prevState,
                      brnDescription: e.target.value,
                    }));
                  }}
                />
              </div>

              <div className="two fields">
                <Dropdown
                  label="Status"
                  module={MODULES.Master}
                  form={FORM}
                  field="brn_status"
                  editMode={editMode}
                  error={currentErrors.brnStatus}
                  resetError={() =>
                    setCurrentErrors((prevState) => {
                      const { brnStatus: _, ...rest } = prevState;
                      return rest;
                    })
                  }
                  placeholder="Select Status"
                  options={dropdown?.statusOpt || []}
                  value={bankBranchData.brnStatus}
                  style={{ height: "20px" }}
                  onChange={(e, data) => {
                    setBankBranchData((prevState) => ({
                      ...prevState,
                      brnStatus: data.value,
                    }));
                  }}
                />
                <Dropdown
                  label="Active"
                  module={MODULES.Master}
                  form={FORM}
                  field="brn_active"
                  editMode={editMode}
                  error={currentErrors.brnActive}
                  resetError={() =>
                    setCurrentErrors((prevState) => {
                      const { brnActive: _, ...rest } = prevState;
                      return rest;
                    })
                  }
                  placeholder="Select Active"
                  options={dropdown?.activeOpt || []}
                  value={bankBranchData.brnActive}
                  style={{ height: "20px" }}
                  onChange={(e, data) => {
                    setBankBranchData((prevState) => ({
                      ...prevState,
                      brnActive: data.value,
                    }));
                  }}
                />
              </div>
            </div>

            {editMode && (
              <InfoList
                title="User Information"
                content={[
                  {
                    title: "Created By",
                    value: bankBranchData.createdBy
                      ? bankBranchData.createdBy
                      : "N/A",
                  },
                  {
                    title: "Created Date",
                    value: bankBranchData.createdDate
                      ? formatFromJsonDate(bankBranchData.createdDate)
                      : "N/A",
                  },
                  {
                    title: "Last Modified By",
                    value: bankBranchData.lastModifiedBy
                      ? bankBranchData.lastModifiedBy
                      : "N/A",
                  },
                  {
                    title: "Last Modified Date",
                    value: bankBranchData.lastModifiedDate
                      ? formatFromJsonDate(bankBranchData.lastModifiedDate)
                      : "N/A",
                  },
                ]}
                Icon={User}
              />
            )}
          </form>
        )}
      </>
    );
  }
);

export default BankBranchAddComponent;
